import { invalidateVersion } from '@redux/situation/situation.reducer';
import { selectSituation } from '@redux/situation/situation.selectors';
import { store } from '@redux/store';
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReAuth } from '@services/baseQueryWithReAuth';
import { C2_URL } from '@services/c2/c2.api';
import { eventToSituationActions, modelSseHandler } from '@utils/sse.utils';
import { EventSourcePolyfill } from 'event-source-polyfill';

import { SseActionEnum, SseMessageEvent } from '@/types/c2/sse.types';

export const sseApi = createApi({
  reducerPath: 'sseApi',
  baseQuery: baseQueryWithReAuth,
  keepUnusedDataFor: 0,
  endpoints: (builder) => ({
    getSituationDiff: builder.query({
      queryFn: async () => ({
        data: null,
      }),
      onCacheEntryAdded: async ({ site, platforms, token }, { dispatch, cacheDataLoaded, cacheEntryRemoved }) => {
        const platformsString = platforms.join(',');
        const url = `${C2_URL}/situations/stream/diff?sites=${site}&platforms=${platformsString}`;

        let eventSource: EventSourcePolyfill | null = null as EventSourcePolyfill | null;
        let retryTimeout: NodeJS.Timeout | null = null as NodeJS.Timeout | null;
        let retryCount = 0;
        let stopped = false;

        await cacheDataLoaded;

        const connect = () => {
          if (stopped) {
            return;
          }

          try {
            eventSource = new EventSourcePolyfill(url, {
              headers: {
                Authorization: token,
                'Cache-Control': 'no-cache',
                Connection: 'keep-alive',
                'Content-Type': 'text/event-stream',
              },
            });

            eventSource.addEventListener('message', (ev: { data: string; lastEventId: string }) => {
              retryCount = 0; // reset on success
              if (ev.lastEventId !== SseActionEnum.HEARTBEAT) {
                const currentSituation = selectSituation(store.getState());
                const actions = eventToSituationActions(currentSituation, ev.data);
                actions.forEach((action) => dispatch(action));
              }
            });

            eventSource.addEventListener('error', (e) => {
              console.error('[SSE DIFF ERROR]', e);

              // clean up current stream
              eventSource?.close();

              if (stopped) {
                return;
              }

              // optional: reset or invalidate API state on first error
              if (retryCount === 0) {
                sseApi.util.resetApiState();
                dispatch(invalidateVersion());
              }

              retryCount++;

              retryTimeout = setTimeout(() => {
                connect();
              }, 5000);
            });
          } catch (err) {
            console.error('[SSE DIFF SETUP ERROR]', err);
          }
        };

        connect();

        await cacheEntryRemoved;

        // Cleanup
        stopped = true;
        if (retryTimeout) clearTimeout(retryTimeout);
        eventSource?.close();
      },
    }),
    getModelsStream: builder.query({
      queryFn: async () => ({ data: null }),
      onCacheEntryAdded: async (
        { site, platforms, token }: { site: string; platforms: string[]; token: string },
        { cacheDataLoaded, cacheEntryRemoved, dispatch },
      ) => {
        const platformsString = platforms.join(',');
        const url = `${C2_URL}/models/stream?sites=${site}&platforms=${platformsString}`;
        let eventSource: EventSourcePolyfill | null = null as EventSourcePolyfill | null;
        let retryTimeout: NodeJS.Timeout | null = null as NodeJS.Timeout | null;
        let stopped = false;

        await cacheDataLoaded;

        const connect = () => {
          if (stopped) {
            return;
          }

          try {
            eventSource = new EventSourcePolyfill(url, {
              headers: {
                Authorization: token,
                'Cache-Control': 'no-cache',
                Connection: 'keep-alive',
                'Content-Type': 'text/event-stream',
              },
            });

            eventSource.addEventListener('message', (event) => {
              const actions = modelSseHandler(event as unknown as SseMessageEvent, site);
              actions.forEach((action) => dispatch(action));
            });

            eventSource.addEventListener('error', (e) => {
              console.error('[SSE MODEL ERROR]', e);

              // clean up current stream
              eventSource?.close();

              if (stopped) {
                return;
              }

              retryTimeout = setTimeout(() => {
                connect();
              }, 5000);
            });
          } catch (err) {
            console.error('[SSE MODEL SETUP ERROR]', err);
          }
        };

        connect();

        await cacheEntryRemoved;

        // Cleanup
        stopped = true;
        if (retryTimeout) clearTimeout(retryTimeout);
        eventSource?.close();
      },
    }),
  }),
});

export const { useGetModelsStreamQuery, useGetSituationDiffQuery } = sseApi;
